/**
 * Movement FSM Reducer — Pure function, no side effects.
 *
 * State: { currentState, context }
 * Context holds per-flow data: selectedExercise, feature flags, tracking state.
 */
import { MovementState, MovementEvent, TRANSITIONS } from './movementFSMConstants';

const INITIAL_CONTEXT = Object.freeze({
  // Feature flags
  isExamineticsEnabled: false,
  isMultiPersonDetectionEnable: false,
  isRealtimeFeedbackROMEnable: false,

  // Flow flags
  hasSeenInstructions: false,
  isAnyModalOpen: false,
  hasNextExercise: false,
  instructionAudioPlayed: false,

  // Per-exercise data
  selectedExercise: null,
  videoDuration: 0,

  // Tracking flags
  wasRecordingInterrupted: false,

  // Selection generation — increments on every EXERCISE_SELECTED,
  // ensures self-loop to same state with same exercise is detected
  _exerciseSelectionGen: 0,
});

export const INITIAL_STATE = Object.freeze({
  currentState: MovementState.IDLE,
  context: { ...INITIAL_CONTEXT },
});

/**
 * Resolve a transition entry. Handles single object and guarded array forms.
 * Returns target state or null if no valid transition.
 */
function resolveTransition(transitionEntry, context, payload) {
  if (!transitionEntry) return null;

  if (Array.isArray(transitionEntry)) {
    for (const candidate of transitionEntry) {
      if (!candidate.guard || candidate.guard(context, payload)) {
        return candidate.to;
      }
    }
    return null;
  }

  if (transitionEntry.guard && !transitionEntry.guard(context, payload)) {
    return null;
  }
  return transitionEntry.to;
}

/**
 * Update context based on event payload.
 */
function updateContext(state, event, payload) {
  if (!payload) return state.context;

  const updates = {};

  switch (event) {
    case MovementEvent.DATA_LOADED:
      if (payload.isExamineticsEnabled !== undefined) updates.isExamineticsEnabled = payload.isExamineticsEnabled;
      if (payload.isMultiPersonDetectionEnable !== undefined) updates.isMultiPersonDetectionEnable = payload.isMultiPersonDetectionEnable;
      if (payload.isRealtimeFeedbackROMEnable !== undefined) updates.isRealtimeFeedbackROMEnable = payload.isRealtimeFeedbackROMEnable;
      if (payload.hasSeenInstructions !== undefined) updates.hasSeenInstructions = payload.hasSeenInstructions;
      if (payload.selectedExercise !== undefined) updates.selectedExercise = payload.selectedExercise;
      if (payload.videoDuration !== undefined) updates.videoDuration = payload.videoDuration;
      break;

    case MovementEvent.EXERCISE_SELECTED:
      if (payload.exercise !== undefined) updates.selectedExercise = payload.exercise;
      if (payload.videoDuration !== undefined) updates.videoDuration = payload.videoDuration;
      if (payload.hasNextExercise !== undefined) updates.hasNextExercise = payload.hasNextExercise;
      updates.isAnyModalOpen = false;
      updates.wasRecordingInterrupted = false;
      updates.instructionAudioPlayed = false;
      updates._exerciseSelectionGen = (state.context._exerciseSelectionGen || 0) + 1;
      break;

    case MovementEvent.EXERCISE_COMPLETED:
      if (payload.hasNextExercise !== undefined) updates.hasNextExercise = payload.hasNextExercise;
      break;

    case MovementEvent.MULTI_PERSON_DETECTED:
      updates.wasRecordingInterrupted = true;
      break;

    case MovementEvent.COUNTDOWN_COMPLETE:
      updates.wasRecordingInterrupted = false;
      break;

    case MovementEvent.INSTRUCTION_AUDIO_COMPLETE:
      updates.instructionAudioPlayed = true;
      break;

    default:
      break;
  }

  if (Object.keys(updates).length === 0) return state.context;
  return { ...state.context, ...updates };
}

/**
 * Main reducer. Returns new state for valid transition, same state for invalid.
 */
export function movementFSMReducer(state, action) {
  const { type, payload } = action;

  // Look up transitions from current state
  const stateTransitions = TRANSITIONS[state.currentState];
  if (!stateTransitions) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[FSM] No transitions defined from state: ${state.currentState}`);
    }
    return state;
  }

  const transitionEntry = stateTransitions[type];
  if (!transitionEntry) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[FSM] Invalid transition: ${state.currentState} + ${type} (no-op)`);
    }
    return state;
  }

  // Update context from payload
  const updatedContext = updateContext(state, type, payload);

  // Resolve transition (handles guards)
  const targetState = resolveTransition(transitionEntry, updatedContext, payload);
  if (!targetState) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[FSM] Guard blocked transition: ${state.currentState} + ${type} (no-op)`);
    }
    return { ...state, context: updatedContext };
  }

  if (process.env.NODE_ENV === 'development') {
    console.log(`[FSM] ${state.currentState} → ${type} → ${targetState}`);
  }

  return {
    ...state,
    context: updatedContext,
    currentState: targetState,
  };
}

/**
 * Update FSM context without a state transition.
 */
export function updateFSMContext(state, updates) {
  return {
    ...state,
    context: { ...state.context, ...updates },
  };
}
